import { createQueryKeys } from '@lukemorales/query-key-factory';

import type { SearchLeadsParams } from './useSearchLeads';

/**
 * Query keys for leads API calls
 *
 * Structure follows orders pattern for consistency:
 * - list (Algolia-based search)
 * - stats (aggregate data)
 * - pipelines (configuration data)
 * - detail (future: individual lead with nested contexts)
 *
 * Example usage:
 * ```ts
 * // Query leads list with filters (table view)
 * const servicesProviderId = useCurrentServicesProviderId();
 * useQuery(queries.leads.list(servicesProviderId, {
 *   filters: { pipeline: 'INBOUND_PIPELINE' },
 *   limit: 20,
 *   page: 0
 * }));
 *
 * // Query leads by stage (Kanban view)
 * useQuery(queries.leads.list(servicesProviderId, {
 *   filters: { stage: 'NEW_LEAD', pipeline: 'INBOUND_PIPELINE' },
 *   limit: 20,
 *   cursor: undefined
 * }));
 *
 * // Future: Query specific lead timeline
 * useQuery(queries.leads.detail(servicesProviderId, leadId)._ctx.timeline);
 *
 * // Invalidate ALL leads queries
 * queryClient.invalidateQueries({
 *   queryKey: queries.leads._def
 * });
 * ```
 */
export const leads = createQueryKeys('leads', {
	// Algolia-based search with filters and pagination
	list: (servicesProviderId: string, params?: SearchLeadsParams) => ({
		queryKey: [servicesProviderId, params],
	}),

	// Aggregate stats (not lead-specific)
	stats: (servicesProviderId: string) => ({
		queryKey: [servicesProviderId],
	}),

	// Pipeline configurations
	pipelines: (servicesProviderId: string) => ({
		queryKey: [servicesProviderId],
	}),

	// Parent context: Lead detail with nested tab contexts (FUTURE)
	detail: (servicesProviderId: string, leadId: string) => ({
		queryKey: [servicesProviderId, leadId],

		contextQueries: {
			timeline: {
				queryKey: null,
			},
			activities: {
				queryKey: null,
			},
			notes: {
				queryKey: null,
			},
		},
	}),

});
